import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';

const URLValidationPage = () => {
  const [summary, setSummary] = useState(null);
  const [validations, setValidations] = useState([]);
  const [batches, setBatches] = useState([]);
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchUrl, setSearchUrl] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch summary
      const summaryRes = await fetch('/api/v1/url-validation/summary');
      const summaryData = await summaryRes.json();
      if (summaryData.status === 'success') {
        setSummary(summaryData);
      }

      // Fetch recent validations
      const validationsRes = await fetch('/api/v1/url-validation/status?limit=50');
      const validationsData = await validationsRes.json();
      if (validationsData.status === 'success') {
        setValidations(validationsData.results);
      }

      // Fetch batches
      const batchesRes = await fetch('/api/v1/url-validation/batches?limit=20');
      const batchesData = await batchesRes.json();
      if (batchesData.status === 'success') {
        setBatches(batchesData.batches);
      }

      // Fetch redirects
      const redirectsRes = await fetch('/api/v1/url-validation/redirects?limit=50');
      const redirectsData = await redirectsRes.json();
      if (redirectsData.status === 'success') {
        setRedirects(redirectsData.redirects);
      }
    } catch (error) {
      console.error('Error fetching URL validation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchValidations = async () => {
    if (!searchUrl.trim()) {
      fetchData();
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/v1/url-validation/status?merchant_url=${encodeURIComponent(searchUrl)}&limit=100`);
      const data = await res.json();
      if (data.status === 'success') {
        setValidations(data.results);
      }
    } catch (error) {
      console.error('Error searching validations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (isValid, isMalformed) => {
    if (isMalformed) {
      return <Badge variant="destructive">Malformed</Badge>;
    }
    if (isValid) {
      return <Badge variant="success" className="bg-green-500">Valid</Badge>;
    }
    return <Badge variant="destructive">Invalid</Badge>;
  };

  const getRiskBadge = (riskLevel) => {
    const variants = {
      'Low': 'default',
      'Medium': 'warning',
      'High': 'destructive'
    };
    const colors = {
      'Low': 'bg-blue-500',
      'Medium': 'bg-yellow-500',
      'High': 'bg-red-500'
    };
    return <Badge variant={variants[riskLevel]} className={colors[riskLevel]}>{riskLevel}</Badge>;
  };

  if (loading && !summary) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Loading URL Validation Data...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6 max-w-[1500px]">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">URL Validation Monitor</h1>
        <Button onClick={fetchData}>Refresh Data</Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Validations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.overall_stats.total_validations}</div>
              <p className="text-xs text-muted-foreground">
                Last: {summary.overall_stats.last_validation ? new Date(summary.overall_stats.last_validation).toLocaleString() : 'N/A'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Valid URLs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{summary.overall_stats.valid_count}</div>
              <p className="text-xs text-muted-foreground">
                {((summary.overall_stats.valid_count / summary.overall_stats.total_validations) * 100).toFixed(1)}% success rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Invalid/Malformed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {summary.overall_stats.invalid_count + summary.overall_stats.malformed_count}
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.overall_stats.invalid_count} invalid, {summary.overall_stats.malformed_count} malformed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Redirects Detected</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{summary.overall_stats.redirect_count}</div>
              <p className="text-xs text-muted-foreground">
                Avg processing: {summary.overall_stats.avg_processing_time.toFixed(3)}s
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Recent Validations</TabsTrigger>
          <TabsTrigger value="batches">Batches</TabsTrigger>
          <TabsTrigger value="redirects">Redirects</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
        </TabsList>

        {/* Recent Validations Tab */}
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Recent URL Validations</CardTitle>
              <CardDescription>Latest URL validation results</CardDescription>
              <div className="flex gap-2 mt-4">
                <Input
                  placeholder="Search by URL..."
                  value={searchUrl}
                  onChange={(e) => setSearchUrl(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchValidations()}
                />
                <Button onClick={searchValidations}>Search</Button>
                <Button variant="outline" onClick={() => { setSearchUrl(''); fetchData(); }}>Clear</Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Normalized URL</TableHead>
                    <TableHead>Redirect</TableHead>
                    <TableHead>Processing Time</TableHead>
                    <TableHead>Validated At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No validation results found
                      </TableCell>
                    </TableRow>
                  ) : (
                    validations.map((validation, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs max-w-xs truncate" title={validation.original_url}>
                          {validation.original_url}
                        </TableCell>
                        <TableCell>{getStatusBadge(validation.is_valid, validation.is_malformed)}</TableCell>
                        <TableCell className="font-mono text-xs max-w-xs truncate" title={validation.normalized_url}>
                          {validation.normalized_url || '-'}
                        </TableCell>
                        <TableCell>
                          {validation.has_redirect ? (
                            <Badge variant="warning" className="bg-yellow-500">Yes</Badge>
                          ) : (
                            <Badge variant="outline">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>{(validation.processing_time_seconds || 0).toFixed(3)}s</TableCell>
                        <TableCell className="text-xs">
                          {validation.validated_at ? new Date(validation.validated_at).toLocaleString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Batches Tab */}
        <TabsContent value="batches">
          <Card>
            <CardHeader>
              <CardTitle>Validation Batches</CardTitle>
              <CardDescription>Batch processing history</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch ID</TableHead>
                    <TableHead>Total URLs</TableHead>
                    <TableHead>Valid</TableHead>
                    <TableHead>Invalid</TableHead>
                    <TableHead>Malformed</TableHead>
                    <TableHead>Redirected</TableHead>
                    <TableHead>Processing Time</TableHead>
                    <TableHead>Created At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        No batch results found
                      </TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs">{batch.id}</TableCell>
                        <TableCell>{batch.total_urls}</TableCell>
                        <TableCell className="text-green-600 font-semibold">{batch.valid_urls}</TableCell>
                        <TableCell className="text-red-600 font-semibold">{batch.invalid_urls}</TableCell>
                        <TableCell className="text-orange-600 font-semibold">{batch.malformed_urls}</TableCell>
                        <TableCell className="text-yellow-600 font-semibold">{batch.redirected_urls}</TableCell>
                        <TableCell>{(batch.processing_time_seconds || 0).toFixed(3)}s</TableCell>
                        <TableCell className="text-xs">
                          {batch.created_at ? new Date(batch.created_at).toLocaleString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Redirects Tab */}
        <TabsContent value="redirects">
          <Card>
            <CardHeader>
              <CardTitle>URL Redirects</CardTitle>
              <CardDescription>Detected URL redirections and risk assessment</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Merchant URL</TableHead>
                    <TableHead>Original URL</TableHead>
                    <TableHead>Final URL</TableHead>
                    <TableHead>Redirect Count</TableHead>
                    <TableHead>Risk Level</TableHead>
                    <TableHead>Detected At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {redirects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No redirects detected
                      </TableCell>
                    </TableRow>
                  ) : (
                    redirects.map((redirect, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-xs max-w-xs truncate" title={redirect.merchant_url}>
                          {redirect.merchant_url}
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-xs truncate" title={redirect.original_url}>
                          {redirect.original_url}
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-xs truncate" title={redirect.final_url}>
                          {redirect.final_url}
                        </TableCell>
                        <TableCell>{redirect.redirect_count}</TableCell>
                        <TableCell>{getRiskBadge(redirect.risk_level)}</TableCell>
                        <TableCell className="text-xs">
                          {redirect.detected_at ? new Date(redirect.detected_at).toLocaleString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors">
          <Card>
            <CardHeader>
              <CardTitle>Recent Validation Errors</CardTitle>
              <CardDescription>URLs with validation errors</CardDescription>
            </CardHeader>
            <CardContent>
              {summary && summary.recent_errors && summary.recent_errors.length > 0 ? (
                <div className="space-y-4">
                  {summary.recent_errors.map((error, idx) => (
                    <div key={idx} className="border rounded-lg p-4 space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="font-mono text-sm font-semibold break-all">{error.original_url}</div>
                        <div className="text-xs text-muted-foreground">
                          {error.validated_at ? new Date(error.validated_at).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                      <div className="space-y-1">
                        {error.errors && error.errors.map((err, errIdx) => (
                          <div key={errIdx} className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                            {err}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  No recent errors found
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default URLValidationPage;
